import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Calendar, Wallet, Sparkles, Ticket, MapPin, Users,
  Plane, CheckCircle2, BedDouble,
} from '../components/ui/Icon';
import { GromlyWordmark } from '../components/brand/GromlyMark';

/* ─── Design tokens (same as login/register) ─────────────────────────── */
const INK      = '#0b1420';
const CREAM    = '#faf7f0';
const CANVAS   = '#f7f7f4';
const SAND     = '#d9b382';
const TEAL     = '#0d9488';
const MUTED    = 'rgba(250,247,240,0.55)';
const MUTED2   = 'rgba(250,247,240,0.7)';

/* ─── Dot-grid + glow (shared with login left panel) ─────────────────── */
const DOT_GRID: React.CSSProperties = {
  backgroundImage: 'radial-gradient(rgba(217,179,130,0.08) 1px, transparent 1px)',
  backgroundSize: '28px 28px',
  maskImage: 'radial-gradient(ellipse 90% 70% at 30% 40%, #000 30%, transparent 70%)',
};
const GLOW: React.CSSProperties = {
  background: 'radial-gradient(circle at center, rgba(217,179,130,0.18), transparent 60%)',
  filter: 'blur(30px)',
};
const SUBTLE_GRID: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(rgba(11,20,32,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(11,20,32,0.025) 1px, transparent 1px)',
  backgroundSize: '24px 24px',
};

/* ─── CTA button (mirrors login primary button) ───────────────────────── */
function CtaButton({ href, children, className = '' }: { href: string; children: React.ReactNode; className?: string }) {
  return (
    <Link
      to={href}
      className={`inline-flex items-center gap-3 font-serif font-medium select-none transition-all duration-150 cursor-pointer ${className}`}
      style={{
        background: INK, color: CREAM, height: 52, paddingLeft: 24, paddingRight: 8,
        borderRadius: 10, fontSize: 16, letterSpacing: -0.3, textDecoration: 'none',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLAnchorElement).style.background = '#000';
        (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-1px)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 10px 24px -10px rgba(11,20,32,0.5)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLAnchorElement).style.background = INK;
        (e.currentTarget as HTMLAnchorElement).style.transform = '';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '';
      }}
    >
      {children}
      <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: SAND, color: INK }}>
        <ArrowRight size={14} />
      </span>
    </Link>
  );
}

function CtaButtonLight({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      to={href}
      className="inline-flex items-center gap-2.5 font-sans font-semibold select-none transition-all duration-150 cursor-pointer"
      style={{
        background: TEAL, color: 'white', height: 52, paddingLeft: 28, paddingRight: 22,
        borderRadius: 10, fontSize: 15, letterSpacing: -0.2, textDecoration: 'none',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLAnchorElement).style.background = '#0f766e';
        (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-1px)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 10px 28px -10px rgba(13,148,136,0.55)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLAnchorElement).style.background = TEAL;
        (e.currentTarget as HTMLAnchorElement).style.transform = '';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '';
      }}
    >
      {children}
      <ArrowRight size={15} />
    </Link>
  );
}

/* ─── Feature card ────────────────────────────────────────────────────── */
function FeatureCard({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="bg-white rounded-2xl border p-6 transition-all duration-200 cursor-default group"
      style={{ borderColor: '#e5e8ee', boxShadow: '0 4px 12px -4px rgba(10,20,40,0.08)' }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px -8px rgba(10,20,40,0.14)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px -4px rgba(10,20,40,0.08)';
        (e.currentTarget as HTMLDivElement).style.transform = '';
      }}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
        style={{ background: '#f0fdfa', color: TEAL }}>
        {icon}
      </div>
      <h3 className="font-sans font-semibold mb-2" style={{ color: INK, fontSize: 15, letterSpacing: -0.2 }}>
        {title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: '#3a4558', lineHeight: 1.65 }}>
        {desc}
      </p>
    </div>
  );
}

/* ─── App mockup (right hero panel) ──────────────────────────────────── */
function AppMockup() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full max-w-sm mx-auto select-none">
      {/* Card */}
      <div className="rounded-2xl overflow-hidden"
        style={{ background: 'white', border: '1px solid #e5e8ee', boxShadow: '0 32px 64px -20px rgba(10,20,40,0.16)' }}>

        {/* Header */}
        <div className="px-6 pt-7 pb-6" style={{ borderBottom: '1px solid #f0f0eb' }}>
          <p className="font-mono text-[10px] uppercase tracking-[1.8px] mb-2" style={{ color: '#9aa3b0' }}>
            {t('landing.mock.nightsLabel', { range: 'SEP 11–20', count: 9 })}
          </p>
          <h4 className="font-serif font-semibold italic text-3xl tracking-tight leading-tight mb-5" style={{ color: INK }}>
            {t('landing.mock.tripTitle')}
          </h4>
          <div className="flex items-center gap-1">
            {[
              { init: 'EE', bg: '#ccfbf1', color: '#0f766e' },
              { init: 'PP', bg: '#ddd6fe', color: '#5b21b6' },
              { init: 'MA', bg: '#fde68a', color: '#92400e' },
            ].map(({ init, bg, color }, i) => (
              <div key={i} className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 border-white"
                style={{ background: bg, color, marginLeft: i > 0 ? -8 : 0, zIndex: 3 - i }}>
                {init}
              </div>
            ))}
            <span className="font-mono text-[10px] ml-2.5" style={{ color: '#9aa3b0' }}>
              {t('landing.mock.moreCount', { count: 2 })}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2" style={{ borderBottom: '1px solid #f0f0eb' }}>
          {[
            { label: t('landing.mock.totalSpent'), prefix: 'EUR', value: '1,247', sub: t('landing.mock.ofBudget', { amount: '2,000' }) },
            { label: t('landing.mock.activities'),  prefix: '',    value: '14',    sub: t('landing.mock.acrossDays', { count: 9 }) },
          ].map(({ label, prefix, value, sub }, i) => (
            <div key={label} className="px-6 py-5" style={{ borderRight: i === 0 ? '1px solid #f0f0eb' : 'none' }}>
              <p className="font-mono text-[9px] uppercase tracking-[1.6px] mb-2" style={{ color: '#9aa3b0' }}>{label}</p>
              <div className="flex items-baseline gap-1 mb-1">
                {prefix && <span className="font-mono text-[10px] font-semibold" style={{ color: SAND }}>{prefix}</span>}
                <span className="font-serif font-semibold text-2xl tracking-tight" style={{ color: INK }}>{value}</span>
              </div>
              <p className="font-mono text-[9px]" style={{ color: '#b0b8c4' }}>{sub}</p>
            </div>
          ))}
        </div>

        {/* Itinerary */}
        <div className="px-6 py-5">
          <p className="font-mono text-[9px] uppercase tracking-[1.8px] mb-4" style={{ color: '#9aa3b0' }}>
            {t('landing.mock.dayHeader', { day: 3, place: 'REYKJAVÍK' })}
          </p>
          <div className="space-y-4">
            {[
              { time: '9:00',  title: 'Blue Lagoon',       cat: '#14b8a6' },
              { time: '12:30', title: 'Hallgrímskirkja',    cat: '#8b5cf6' },
              { time: '15:00', title: 'Harpa Concert Hall', cat: '#f97316' },
            ].map((act, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: act.cat }} />
                <span className="font-mono text-[10px] tabular-nums" style={{ color: '#9aa3b0', minWidth: 34 }}>{act.time}</span>
                <span className="text-sm font-medium" style={{ color: INK }}>{act.title}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating AI chip */}
      <div className="absolute -right-5 top-10 flex items-center gap-2 px-3.5 py-2.5 rounded-xl"
        style={{ background: INK, boxShadow: '0 12px 32px -8px rgba(11,20,32,0.45)' }}>
        <Sparkles size={11} color={SAND} />
        <span className="font-mono text-[10px] tracking-wide" style={{ color: CREAM }}>{t('landing.mock.routeOptimized')}</span>
      </div>

      {/* Floating balance chip */}
      <div className="absolute -left-6 bottom-14 flex items-center gap-2 px-3.5 py-2.5 rounded-xl"
        style={{ background: 'white', border: '1px solid #e5e8ee', boxShadow: '0 12px 32px -8px rgba(11,20,32,0.12)' }}>
        <CheckCircle2 size={11} color={TEAL} />
        <span className="font-mono text-[10px]" style={{ color: '#3a4558' }}>{t('landing.mock.splitSettled', { count: 3 })}</span>
      </div>
    </div>
  );
}

/* ─── Route mockup for AI section ────────────────────────────────────── */
function RouteMockup() {
  const { t } = useTranslation();
  const stops = [
    { n: 1, title: 'Airbnb check-in', time: '15:00', mode: 'walk', dist: '0.4 km' },
    { n: 2, title: 'Blue Lagoon', time: '10:00', mode: 'drive', dist: '12 km' },
    { n: 3, title: 'Skógafoss waterfall', time: '14:30', mode: 'drive', dist: '30 km' },
    { n: 4, title: 'Dinner · Matur og Drykkur', time: '20:00', mode: 'walk', dist: '0.8 km' },
  ];

  return (
    <div className="rounded-2xl border overflow-hidden max-w-xs mx-auto lg:mx-0"
      style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}>
      <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <Sparkles size={13} color={SAND} />
        <span className="font-mono text-[10px] tracking-[1.4px] uppercase" style={{ color: SAND }}>{t('landing.mock.aiOptimized', { day: 3 })}</span>
      </div>
      <div className="px-4 py-3 space-y-0">
        {stops.map((stop, i) => (
          <div key={i} className="flex items-start gap-3">
            {/* Rail */}
            <div className="flex flex-col items-center pt-0.5">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 font-mono text-[11px] font-bold"
                style={{ background: TEAL, color: 'white' }}>
                {stop.n}
              </div>
              {i < stops.length - 1 && (
                <div className="w-px flex-1 my-1" style={{ background: 'rgba(20,184,166,0.3)', minHeight: 20 }} />
              )}
            </div>
            {/* Content */}
            <div className="pb-3 flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium truncate" style={{ color: CREAM }}>{stop.title}</p>
                <span className="font-mono text-[11px] flex-shrink-0" style={{ color: MUTED }}>{stop.time}</span>
              </div>
              {i < stops.length - 1 && (
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="font-mono text-[11px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.07)', color: MUTED }}>
                    {stop.mode === 'walk' ? '🚶' : '🚗'} {stop.dist}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────────────── */
export default function LandingPage() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const navLinks = [
    { label: t('landing.nav.features'), href: '#features' },
    { label: t('landing.nav.ai'), href: '#ai' },
    { label: t('landing.nav.community'), href: '#collab' },
    { label: t('landing.nav.marketplace'), href: '#marketplace' },
  ];

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── NAVBAR ──────────────────────────────────────────────────────── */}
      <nav
        className="fixed top-0 inset-x-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(11,20,32,0.96)' : INK,
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          boxShadow: scrolled ? '0 1px 0 rgba(255,255,255,0.05)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(({ label, href }) => (
              <a key={href} href={href}
                className="text-sm font-medium transition-colors duration-150"
                style={{ color: 'rgba(250,247,240,0.6)', textDecoration: 'none' }}
                onMouseEnter={e => ((e.target as HTMLAnchorElement).style.color = CREAM)}
                onMouseLeave={e => ((e.target as HTMLAnchorElement).style.color = 'rgba(250,247,240,0.6)')}
              >
                {label}
              </a>
            ))}
          </div>

          {/* Auth buttons */}
          <div className="flex items-center gap-3">
            <Link to="/login"
              className="hidden sm:inline-flex text-sm font-medium px-4 py-2 rounded-xl transition-all duration-150"
              style={{ color: 'rgba(250,247,240,0.75)', textDecoration: 'none' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.08)';
                (e.currentTarget as HTMLAnchorElement).style.color = CREAM;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = '';
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(250,247,240,0.75)';
              }}
            >
              {t('landing.nav.signIn')}
            </Link>
            <Link to="/register"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl transition-all duration-150"
              style={{ background: TEAL, color: 'white', textDecoration: 'none' }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.background = '#0f766e')}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.background = TEAL)}
            >
              {t('landing.nav.getStarted')}
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr]" style={{ paddingTop: 64 }}>

        {/* Left — dark editorial */}
        <div className="relative flex flex-col justify-center px-8 sm:px-14 py-20 overflow-hidden"
          style={{ background: INK, color: CREAM }}>
          {/* Dot grid */}
          <div className="absolute inset-0 pointer-events-none" style={DOT_GRID} />
          {/* Glow */}
          <div className="absolute -right-24 -bottom-16 w-96 h-96 rounded-full pointer-events-none" style={GLOW} />

          {/* Logo — fixed top */}
          <Link to="/" className="absolute top-8 left-8 sm:left-14 z-10 flex items-center gap-3" style={{ textDecoration: 'none' }}>
            <GromlyWordmark size={38} dark />
          </Link>

          <div className="relative z-10 max-w-xl">
            {/* Eyebrow */}
            <div className="flex items-center gap-2.5 font-mono text-[10.5px] tracking-[2px] uppercase mb-8" style={{ color: SAND }}>
              <span className="w-8 h-px" style={{ background: SAND, opacity: 0.6 }} />
              {t('landing.hero.eyebrow')}
            </div>

            {/* Headline */}
            <h1 className="font-serif font-light italic leading-none tracking-tight mb-5"
              style={{ fontSize: 'clamp(52px, 6vw, 76px)' }}>
              <span style={{ color: CREAM, fontStyle: 'normal' }}>{t('landing.hero.headline1')}</span>
              <br />
              <span style={{ color: SAND, fontStyle: 'italic' }}>{t('landing.hero.headline2')}</span>
            </h1>

            {/* Subtext */}
            <p className="font-sans text-base leading-relaxed mb-10"
              style={{ color: MUTED2, lineHeight: 1.75, maxWidth: 440 }}>
              {t('landing.hero.subheading')}
            </p>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-2 mb-10">
              {[
                { icon: <Calendar size={12} />, label: t('landing.hero.pills.itinerary') },
                { icon: <Wallet size={12} />, label: t('landing.hero.pills.expenses') },
                { icon: <Sparkles size={12} />, label: t('landing.hero.pills.aiRoutes') },
                { icon: <Plane size={12} />, label: t('landing.hero.pills.bookings') },
                { icon: <Users size={12} />, label: t('landing.hero.pills.collaborative') },
              ].map(({ icon, label }) => (
                <div key={label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: CREAM }}>
                  <span style={{ color: SAND }}>{icon}</span>
                  <span className="font-mono text-[10px] tracking-wide">{label}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4">
              <CtaButtonLight href="/register">
                {t('landing.hero.ctaPrimary')}
              </CtaButtonLight>
              <Link to="/login"
                className="font-sans font-medium text-sm flex items-center gap-1.5 transition-opacity duration-150"
                style={{ color: MUTED2, textDecoration: 'none' }}
                onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.color = CREAM)}
                onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.color = MUTED2)}
              >
                {t('landing.hero.ctaSecondary')} <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* Bottom ticker */}
          <div className="relative z-10 mt-20 pt-6 border-t flex items-center gap-3 overflow-hidden"
            style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
            <span className="font-mono text-[11px] tracking-[1.6px] uppercase whitespace-nowrap" style={{ color: MUTED }}>
              {t('landing.ticker.label')}
            </span>
            {['Reykjavík', 'Tokyo', 'Lisbon', 'Marrakech', 'Kyoto', 'Barcelona', 'Patagonia'].map((city, i) => (
              <span key={i} className="font-mono text-[11px] tracking-[1.4px] uppercase whitespace-nowrap" style={{ color: 'rgba(250,247,240,0.35)' }}>
                {city} ·
              </span>
            ))}
          </div>
        </div>

        {/* Right — cream with mockup */}
        <div className="hidden lg:flex items-center justify-center px-10 py-16 relative"
          style={{ background: CREAM }}>
          <div className="absolute inset-0 pointer-events-none" style={SUBTLE_GRID} />
          <div className="relative z-10 w-full">
            <AppMockup />
          </div>
        </div>
      </div>

      {/* ── FEATURES ────────────────────────────────────────────────────── */}
      <section id="features" style={{ background: CANVAS }} className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="max-w-2xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase mb-5"
              style={{ color: TEAL }}>
              <span className="w-6 h-px" style={{ background: TEAL }} />
              {t('landing.features.eyebrow')}
              <span className="w-6 h-px" style={{ background: TEAL }} />
            </div>
            <h2 className="font-serif font-semibold leading-tight mb-4"
              style={{ fontSize: 'clamp(36px, 4vw, 52px)', letterSpacing: -2, color: INK }}>
              {t('landing.features.headlineLine1')}
              <br />{t('landing.features.headlineLine2Pre')} <span style={{ color: TEAL }}>{t('landing.features.headlineLine2Highlight')}</span>
            </h2>
            <p className="text-base leading-relaxed" style={{ color: '#3a4558', lineHeight: 1.7 }}>
              {t('landing.features.description')}
            </p>
          </div>

          {/* Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              icon={<Calendar size={18} />}
              title={t('landing.features.cards.itinerary.title')}
              desc={t('landing.features.cards.itinerary.desc')}
            />
            <FeatureCard
              icon={<Wallet size={18} />}
              title={t('landing.features.cards.expenses.title')}
              desc={t('landing.features.cards.expenses.desc')}
            />
            <FeatureCard
              icon={<Sparkles size={18} />}
              title={t('landing.features.cards.ai.title')}
              desc={t('landing.features.cards.ai.desc')}
            />
            <FeatureCard
              icon={<Ticket size={18} />}
              title={t('landing.features.cards.bookings.title')}
              desc={t('landing.features.cards.bookings.desc')}
            />
            <FeatureCard
              icon={<MapPin size={18} />}
              title={t('landing.features.cards.map.title')}
              desc={t('landing.features.cards.map.desc')}
            />
            <FeatureCard
              icon={<Users size={18} />}
              title={t('landing.features.cards.collaborative.title')}
              desc={t('landing.features.cards.collaborative.desc')}
            />
          </div>
        </div>
      </section>

      {/* ── AI SECTION ──────────────────────────────────────────────────── */}
      <section id="ai" className="py-24 relative overflow-hidden" style={{ background: INK }}>
        {/* Dot grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(rgba(217,179,130,0.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />
        <div className="absolute -left-32 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full pointer-events-none" style={GLOW} />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div>
              <div className="flex items-center gap-2.5 font-mono text-[10.5px] tracking-[2px] uppercase mb-7" style={{ color: SAND }}>
                <span className="w-8 h-px" style={{ background: SAND, opacity: 0.6 }} />
                {t('landing.ai.eyebrow')}
              </div>
              <h2 className="font-serif font-light leading-tight mb-6"
                style={{ fontSize: 'clamp(36px, 4vw, 52px)', letterSpacing: -2 }}>
                <span style={{ color: CREAM, fontStyle: 'normal' }}>{t('landing.ai.headline1')}</span>
                <br />
                <span style={{ color: SAND, fontStyle: 'italic' }}>{t('landing.ai.headline2')}</span>
              </h2>
              <p className="font-sans text-base leading-relaxed mb-8" style={{ color: MUTED2, lineHeight: 1.7 }}>
                {t('landing.ai.description')}
              </p>

              {/* Chips */}
              <div className="flex flex-wrap gap-3 mb-10">
                {[
                  t('landing.ai.chips.route'),
                  t('landing.ai.chips.hints'),
                  t('landing.ai.chips.mode'),
                  t('landing.ai.chips.grouping'),
                ].map(c => (
                  <div key={c} className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                    style={{ border: `1px solid ${SAND}40`, color: CREAM }}>
                    <CheckCircle2 size={11} color={TEAL} />
                    <span className="font-mono text-[10px] tracking-wide">{c}</span>
                  </div>
                ))}
              </div>

              <Link to="/register"
                className="inline-flex items-center gap-2 font-mono text-sm tracking-wide transition-opacity duration-150"
                style={{ color: SAND, textDecoration: 'none' }}
                onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.7')}
                onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
              >
                {t('landing.ai.cta')} <ArrowRight size={14} />
              </Link>
            </div>

            {/* Mockup */}
            <div className="flex justify-center lg:justify-end">
              <RouteMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ── COLLABORATIVE SECTION ───────────────────────────────────────── */}
      <section id="collab" className="py-24" style={{ background: CREAM }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Visual */}
            <div className="order-2 lg:order-1">
              <div className="rounded-2xl border p-6 max-w-sm mx-auto"
                style={{ background: 'white', borderColor: '#e5e8ee', boxShadow: '0 4px 12px -4px rgba(10,20,40,0.1)' }}>
                {/* Header */}
                <p className="font-mono text-[11px] uppercase tracking-[1.4px] mb-4" style={{ color: '#6b7689' }}>
                  {t('landing.mock.whoOwesWhom')}
                </p>
                {/* Debt rows */}
                {[
                  { from: 'EE', fromName: 'Emma', to: 'PP', toName: 'Pau', amount: '€ 448.38', color: '#ddd6fe', tc: '#5b21b6' },
                  { from: 'MA', fromName: 'Marc', to: 'EE', toName: 'Emma', amount: '€ 211.50', color: '#fde68a', tc: '#92400e' },
                ].map((d, i) => (
                  <div key={i} className="flex items-center gap-3 mb-3 last:mb-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: d.color, color: d.tc }}>{d.from}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs" style={{ color: INK }}>{d.fromName} → {d.toName}</p>
                      <p className="font-serif font-medium text-lg tracking-tight" style={{ color: INK }}>{d.amount}</p>
                    </div>
                    <div className="px-2.5 py-1 rounded-lg text-[10px] font-semibold cursor-pointer transition-colors duration-150"
                      style={{ background: '#0d9488', color: 'white', fontFamily: 'Inter' }}>
                      {t('landing.mock.settle')}
                    </div>
                  </div>
                ))}
                {/* Settled */}
                <div className="mt-4 pt-4 border-t flex items-center justify-between" style={{ borderColor: '#f0f0eb' }}>
                  <span className="font-mono text-[11px] uppercase tracking-[1.4px]" style={{ color: '#d6dae2' }}>{t('landing.mock.settledCount', { count: 2 })}</span>
                  <CheckCircle2 size={14} color="#14b8a6" />
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2.5 font-mono text-[10.5px] tracking-[2px] uppercase mb-7" style={{ color: TEAL }}>
                <span className="w-8 h-px" style={{ background: TEAL, opacity: 0.6 }} />
                {t('landing.groups.eyebrow')}
              </div>
              <h2 className="font-serif font-semibold leading-tight mb-6"
                style={{ fontSize: 'clamp(36px, 4vw, 52px)', letterSpacing: -2, color: INK }}>
                {t('landing.groups.headline1')}
                <br />
                <span style={{ color: TEAL }}>{t('landing.groups.headline2')}</span>
              </h2>
              <p className="font-sans text-base leading-relaxed mb-8" style={{ color: '#3a4558', lineHeight: 1.7 }}>
                {t('landing.groups.description')}
              </p>

              {/* Feature list */}
              <div className="space-y-3">
                {[
                  t('landing.groups.features.invite'),
                  t('landing.groups.features.split'),
                  t('landing.groups.features.settle'),
                  t('landing.groups.features.sync'),
                ].map(f => (
                  <div key={f} className="flex items-center gap-3">
                    <CheckCircle2 size={15} color={TEAL} className="flex-shrink-0" />
                    <span className="text-sm font-medium" style={{ color: '#3a4558' }}>{f}</span>
                  </div>
                ))}
              </div>

              {/* Avatars */}
              <div className="flex items-center gap-3 mt-8">
                <div className="flex -space-x-2">
                  {[
                    ['EE', '#ccfbf1', '#0f766e'],
                    ['PP', '#ddd6fe', '#5b21b6'],
                    ['MA', '#fde68a', '#92400e'],
                  ].map(([init, bg, tc], i) => (
                    <div key={i} className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white"
                      style={{ background: bg, color: tc }}>
                      {init}
                    </div>
                  ))}
                </div>
                <p className="text-sm" style={{ color: '#6b7689' }}>
                  <span className="font-semibold" style={{ color: INK }}>{t('landing.groups.morePrefix', { count: 2 })}</span> {t('landing.groups.morePlanning')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MARKETPLACE SECTION ─────────────────────────────────────────── */}
      <section id="marketplace" className="py-24" style={{ background: CANVAS }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div>
              <div className="flex items-center gap-2.5 font-mono text-[10.5px] tracking-[2px] uppercase mb-7" style={{ color: TEAL }}>
                <span className="w-8 h-px" style={{ background: TEAL, opacity: 0.6 }} />
                {t('landing.marketplace.eyebrow')}
              </div>
              <h2 className="font-serif font-semibold leading-tight mb-6"
                style={{ fontSize: 'clamp(36px, 4vw, 52px)', letterSpacing: -2, color: INK }}>
                {t('landing.marketplace.headline1')}
                <br />
                <span style={{ color: TEAL }}>{t('landing.marketplace.headline2')}</span>
              </h2>
              <p className="font-sans text-base leading-relaxed mb-8" style={{ color: '#3a4558', lineHeight: 1.7 }}>
                {t('landing.marketplace.description')}
              </p>
              <div className="space-y-3 mb-8">
                {[
                  t('landing.marketplace.features.browse'),
                  t('landing.marketplace.features.clone'),
                  t('landing.marketplace.features.ratings'),
                  t('landing.marketplace.features.follow'),
                ].map(f => (
                  <div key={f} className="flex items-center gap-3">
                    <CheckCircle2 size={15} color={TEAL} className="flex-shrink-0" />
                    <span className="text-sm font-medium" style={{ color: '#3a4558' }}>{f}</span>
                  </div>
                ))}
              </div>
              <Link to="/register"
                className="inline-flex items-center gap-2 font-mono text-sm tracking-wide transition-opacity duration-150"
                style={{ color: TEAL, textDecoration: 'none' }}
                onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.7')}
                onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
              >
                {t('landing.marketplace.cta')} <ArrowRight size={14} />
              </Link>
            </div>

            {/* Mockup: trip cards grid */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { title: 'Kyoto in Fall', nights: 7, author: 'EE', country: 'Japan', rating: 4.9, tags: [t('landing.marketplace.tripTags.nature'), t('landing.marketplace.tripTags.culture')], ab: '#ccfbf1', ac: '#0f766e' },
                { title: 'Patagonia Trek', nights: 12, author: 'MA', country: 'Chile', rating: 4.8, tags: [t('landing.marketplace.tripTags.adventure'), t('landing.marketplace.tripTags.hiking')], ab: '#ddd6fe', ac: '#5b21b6' },
                { title: 'Amalfi Coast', nights: 5, author: 'PP', country: 'Italy', rating: 4.7, tags: [t('landing.marketplace.tripTags.beach'), t('landing.marketplace.tripTags.food')], ab: '#fde68a', ac: '#92400e' },
                { title: 'Marrakech Medina', nights: 4, author: 'LB', country: 'Morocco', rating: 4.9, tags: [t('landing.marketplace.tripTags.culture'), t('landing.marketplace.tripTags.souks')], ab: '#fee2e2', ac: '#991b1b' },
              ].map((trip, i) => (
                <div key={i} className="rounded-xl border p-4 cursor-pointer transition-all duration-200"
                  style={{ background: 'white', borderColor: '#e5e8ee', boxShadow: '0 2px 8px -2px rgba(10,20,40,0.06)' }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px -8px rgba(10,20,40,0.14)';
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px -2px rgba(10,20,40,0.06)';
                    (e.currentTarget as HTMLDivElement).style.transform = '';
                  }}
                >
                  <div className="flex items-start justify-between mb-2.5">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                      style={{ background: trip.ab, color: trip.ac }}>
                      {trip.author}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[11px]" style={{ color: '#f59e0b' }}>★</span>
                      <span className="font-mono text-[10px] font-medium" style={{ color: '#6b7689' }}>{trip.rating}</span>
                    </div>
                  </div>
                  <h4 className="font-sans font-semibold mb-0.5" style={{ color: INK, fontSize: 13, letterSpacing: -0.2 }}>{trip.title}</h4>
                  <p className="font-mono text-[10px] mb-2.5" style={{ color: '#6b7689' }}>{t('landing.marketplace.nights', { country: trip.country, count: trip.nights })}</p>
                  <div className="flex flex-wrap gap-1">
                    {trip.tags.map(tag => (
                      <span key={tag} className="px-1.5 py-0.5 rounded-md font-mono text-[9px]"
                        style={{ background: '#f0fdfa', color: TEAL }}>{tag}</span>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: '#f0f0eb' }}>
                    <span className="font-mono text-[10px]" style={{ color: '#6b7689' }}>{t('landing.marketplace.cloneTrip')}</span>
                    <ArrowRight size={11} color={TEAL} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ─────────────────────────────────────────────────── */}
      <section className="py-28 relative overflow-hidden" style={{ background: INK }}>
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(rgba(217,179,130,0.07) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none opacity-60" style={GLOW} />

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2.5 font-mono text-[10.5px] tracking-[2px] uppercase mb-8" style={{ color: SAND }}>
            <span className="w-6 h-px" style={{ background: SAND, opacity: 0.6 }} />
            {t('landing.bottomCta.eyebrow')}
            <span className="w-6 h-px" style={{ background: SAND, opacity: 0.6 }} />
          </div>

          <h2 className="font-serif font-light leading-tight mb-6"
            style={{ fontSize: 'clamp(42px, 5vw, 64px)', letterSpacing: -3 }}>
            <span style={{ color: CREAM, fontStyle: 'normal' }}>{t('landing.bottomCta.headline1')}</span>
            <br />
            <span style={{ color: SAND, fontStyle: 'italic' }}>{t('landing.bottomCta.headline2')}</span>
          </h2>

          <p className="font-sans text-base leading-relaxed mb-10" style={{ color: MUTED2, lineHeight: 1.7 }}>
            {t('landing.bottomCta.description')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <CtaButtonLight href="/register">
              {t('landing.bottomCta.button')}
            </CtaButtonLight>
            <Link to="/login"
              className="font-sans font-medium text-sm flex items-center gap-1.5 transition-opacity duration-150"
              style={{ color: MUTED2, textDecoration: 'none' }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.color = CREAM)}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.color = MUTED2)}
            >
              {t('landing.bottomCta.haveAccount')} <ArrowRight size={14} />
            </Link>
          </div>

          <p className="font-mono text-[10px] tracking-[1.4px] uppercase mt-6" style={{ color: 'rgba(250,247,240,0.3)' }}>
            {t('landing.bottomCta.fineprint')}
          </p>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="py-12 border-t" style={{ background: INK, borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            {/* Logo */}
            <GromlyWordmark size={30} dark />

            {/* Links */}
            <div className="flex items-center gap-6">
              {[
                { label: t('landing.footer.privacy'), href: '/privacy' },
                { label: t('landing.footer.terms'), href: '/terms' },
                { label: t('landing.footer.legal'), href: '/legal' },
              ].map(({ label, href }) => (
                <Link key={href} to={href}
                  className="font-mono text-[10px] tracking-[1.2px] uppercase transition-colors duration-150"
                  style={{ color: 'rgba(250,247,240,0.35)', textDecoration: 'none' }}
                  onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.color = 'rgba(250,247,240,0.7)')}
                  onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.color = 'rgba(250,247,240,0.35)')}
                >
                  {label}
                </Link>
              ))}
              <a href="mailto:contact@gromly.io"
                className="font-mono text-[10px] tracking-[1.2px] transition-colors duration-150"
                style={{ color: 'rgba(250,247,240,0.35)', textDecoration: 'none' }}
                onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.color = 'rgba(250,247,240,0.7)')}
                onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.color = 'rgba(250,247,240,0.35)')}
              >
                contact@gromly.io
              </a>
            </div>

            {/* Copyright */}
            <p className="font-mono text-[11px] tracking-[1.2px]" style={{ color: 'rgba(250,247,240,0.2)' }}>
              {t('landing.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
